import { createQueryKeyStore } from '@lukemorales/query-key-factory';
import { QueryClient } from '@tanstack/react-query';
import { FetchInstalledEquipmentsParams } from '@/api/appSolutionBuilder/filterUnit';
import { CoordinatesRequest } from '@/api/location/types';
import { ProductListPageQuery } from '@/api/product/search';
import queryStaleTimes from '@/api/queryStaleTimes';
import {
	GlobalListType,
	type ListCustomerParams,
	PriceRequest,
	SearchSettingsResponse,
} from '@/generated/data.schemas';
import { ArticleListQuery } from './article/search';
import { Filter } from './product/types';

// Type is redefined here, since builder does not currently export it
export interface UserAttributes {
	readonly [key: string]:
		| undefined
		| string
		| readonly string[]
		| boolean
		| readonly boolean[]
		| number
		| number[]
		| Readonly<Record<string, unknown>>;
}

// This is a helper function to create query keys for react-query.
// You only need to use it for the queries that are not generated by Orval.
export const queryKeys = createQueryKeyStore({
	location: {
		coordinates: (params: CoordinatesRequest) => [params.address, params.key],
	},
	article: {
		search: (query: ArticleListQuery, settings?: SearchSettingsResponse) => [query, settings],
	},
	product: {
		detail: (params: {
			readonly searchApiHost: string;
			readonly searchToken: string;
			readonly searchSegment: string;
			readonly searchableIdsFilterId: string;
			readonly detailsScopeId: string;
			readonly productId: string;
		}) => [
			params.searchApiHost,
			params.searchToken,
			params.searchSegment,
			params.searchableIdsFilterId,
			params.detailsScopeId,
			params.productId,
		],
		band: (params: {
			readonly searchApiHost: string;
			readonly searchToken: string;
			readonly searchSegment: string;
			readonly bandsScopeId: string;
			readonly productIds: string[];
		}) => [params.searchApiHost, params.searchToken, params.searchSegment, params.bandsScopeId, params.productIds],
		search: (query: ProductListPageQuery, settings?: SearchSettingsResponse) => [query, settings],
		quickSearch: (params: {
			readonly searchApiHost: string;
			readonly searchToken: string;
			readonly searchSegment: string;
			readonly quickSearchScopeId: string;
			readonly phrase: string;
		}) => [
			params.searchApiHost,
			params.searchToken,
			params.searchSegment,
			params.quickSearchScopeId,
			params.phrase,
		],
		dynamicSearch: (query: ProductListPageQuery, settings?: SearchSettingsResponse) => [query, settings],
		suggestions: (segment: string, phrase: string) => [segment, phrase],
		article: (query: ArticleListQuery, settings?: SearchSettingsResponse) => [query, settings],
	},
	productCategory: {
		search: (phrase?: string, settings?: SearchSettingsResponse) => [phrase || '', settings],
	},
	applicationDetail: {
		installedEquipments: (params: FetchInstalledEquipmentsParams) => [
			params.searchApiHost,
			params.searchToken,
			params.searchSegment,
			params.installedEquipmentScopeId,
			params.phrase,
		],
	},
	appSolutionBuilder: {
		search: (
			segmentationId?: number,
			segment?: string,
			application?: string,
			subApplicationValue?: string,
			specifications?: Filter[],
			settings?: SearchSettingsResponse,
		) => [segmentationId, segment, application, subApplicationValue, specifications, settings],
		filterUnit: (params: {
			readonly searchApiHost: string;
			readonly searchToken: string;
			readonly searchSegment: string;
			readonly searchableIdsFilterId: string;
			readonly detailsScopeId: string;
			readonly filterUnitId: string;
		}) => [
			params.searchApiHost,
			params.searchToken,
			params.searchSegment,
			params.searchableIdsFilterId,
			params.detailsScopeId,
			params.filterUnitId,
		],
		associatedProducts: (
			categoryNames: string[],
			productMasterIds: string[],
			settings?: SearchSettingsResponse,
		) => [categoryNames, productMasterIds, settings],
	},
	customers: {
		list: (segment: number, queryParams?: ListCustomerParams) => [segment, queryParams],
		search: (segmentationId: number, query: string) => [segmentationId, query],
		search2: (segmentationId?: number) => [segmentationId],
	},
	customer: {
		detail: (params: { id: number }) => [params.id],
	},
	globalList: {
		list: (segmentationId: number, type: GlobalListType) => [segmentationId, type],
		all: (segmentationId: number) => [segmentationId],
	},
	brands: {
		list: (segmentationId: number) => [segmentationId],
	},
	cms: {
		page: (params: { url?: string; locale: string; modelName: string; userAttributes?: UserAttributes }) => [
			params.url ?? '',
			params.locale,
			params.modelName,
			params.userAttributes ?? {},
		],
		taggedPages: (params: {
			locale: string;
			modelName: string;
			tags: readonly string[];
			maxItems: number;
			sortOrder: 'newest' | 'oldest';
			userAttributes?: UserAttributes;
		}) => [
			params.locale,
			params.modelName,
			params.tags,
			params.maxItems,
			params.sortOrder,
			params.userAttributes ?? {},
		],
		// Temporary, removed together with @/api/cms/blogFilterCategories
		blogFilterCategories: (params: { locale: string }) => [params.locale],
		// Temporary, removed together with @/api/cms/knowledgeHubArticles
		knowledgeHubFeaturedArticle: (params: {
			locale: string;
			modelName: string;
			path: string;
			categoryIds: readonly string[];
		}) => [params.locale, params.modelName, params.path, params.categoryIds],
		// Temporary, removed together with @/api/cms/knowledgeHubArticles
		knowledgeHubArticles: (params: { locale: string; modelName: string; categoryIds: readonly string[] }) => [
			params.locale,
			params.modelName,
			params.categoryIds,
		],
		menu: (locale: string, modelName: string, userAttributes?: UserAttributes) => [
			locale,
			modelName,
			userAttributes,
		],
		megaMenu: (locale: string, modelName: string, userAttributes?: UserAttributes) => [
			locale,
			modelName,
			userAttributes,
		],
		hrefLangs: (modelName: string, hreflangGroupName: string, userAttributes?: UserAttributes) => [
			modelName,
			hreflangGroupName,
			userAttributes,
		],
	},
	prices: {
		all: (segmentationId: number, priceRequest: PriceRequest) => [segmentationId, priceRequest],
	},
});

export const setAllQueryDefaults = (queryClient: QueryClient): void => {
	// You only need to set default stale times for queries that are not generated by Orval.
	// The rest are set in the orval.config.ts file.
	queryClient.setQueryDefaults(queryKeys.product._def, { staleTime: queryStaleTimes.product });
	queryClient.setQueryDefaults(queryKeys.productCategory._def, { staleTime: queryStaleTimes.productCategory });
	queryClient.setQueryDefaults(queryKeys.customers._def, { staleTime: queryStaleTimes.customers });
	queryClient.setQueryDefaults(queryKeys.customer._def, { staleTime: queryStaleTimes.customer });
	queryClient.setQueryDefaults(queryKeys.cms._def, { staleTime: queryStaleTimes.cms });
	queryClient.setQueryDefaults(queryKeys.globalList._def, { staleTime: queryStaleTimes.globalList });
	queryClient.setQueryDefaults(queryKeys.appSolutionBuilder._def, { staleTime: queryStaleTimes.appSolutionBuilder });
	queryClient.setQueryDefaults(queryKeys.appSolutionBuilder.filterUnit._def, { staleTime: 0 }); // No caching for filter unit
	queryClient.setQueryDefaults(queryKeys.prices._def, { staleTime: 0 }); // No caching for prices
};
